// @ts-strict-ignore
import React, { useCallback, useEffect, useRef, useState } from "react";

import { StorageManager } from "@lib/storageManager";
import { Subscribable } from "@lib/subscribable";

import { BrazeDashboardConfigContext } from "@services/brazeDashboardConfig";

import type { BrazeDashboardConfigProviderProps } from "./types";

const configChangePubSub = new Subscribable<"config-change">();

const applyFFOverrides = (
  config: BrazeDashboardConfigProviderProps["config"],
  disableFeatureFlipperLocalStorageOverrides: boolean
) => {
  if (disableFeatureFlipperLocalStorageOverrides) {
    return config;
  }

  if (
    !StorageManager.get(StorageManager.KEYS.BRAZE_DEBUG_PERSIST_FF_OVERRIDES) ||
    !StorageManager.get(StorageManager.KEYS.ENABLE_BRAZE_DEBUG_TOOL)
  ) {
    return config;
  }

  const overrides: Partial<BrazeDashboardConfigProviderProps["config"]["features"]> | undefined =
    StorageManager.get(StorageManager.KEYS.BRAZE_DEBUG_FF_OVERRIDES);
  if (overrides) {
    return {
      ...config,
      features: {
        ...config.features,
        ...overrides,
      },
    };
  }
  return config;
};

/**
 * Context provider for the Braze Dashboard config object
 * @note uses the under normal circumstanaces, the window property where we store configs
 * should not change. However, this has provisions for mutating config properties for
 * debugging purposes. Also, due to there being multiple root React nodes, this needs to
 * do a pub/sub so its sibling instances are availed of any config changes. At some point
 * in the future when all the nodes are consolidated, this pub/sub will be obviated.
 */
export const BrazeDashboardConfigProvider = ({
  children,
  config = window.brazeDashboardConfig,
  disableFeatureFlipperLocalStorageOverrides = false,
}: BrazeDashboardConfigProviderProps): React.ReactElement => {
  // it is vital to maintain a separate object reference of `config` since all of this
  // relies on bog-standard `useState` for invalidation
  const [state, setState] = useState(() => ({
    ...applyFFOverrides(config, disableFeatureFlipperLocalStorageOverrides),
  }));

  const firstMount = useRef(true);

  // effect to respond to changes in the `config` prop after the first mounting event
  // this should not change in practice, and only helps to satisfy test conditions that
  // rely on the `config` prop changing over its lifetime
  useEffect(() => {
    if (firstMount.current) {
      firstMount.current = false;
      return;
    }

    setState({ ...applyFFOverrides(config, disableFeatureFlipperLocalStorageOverrides) });
  }, [config, disableFeatureFlipperLocalStorageOverrides]);

  const handleSetState = useCallback((field, value) => {
    window.brazeDashboardConfig[field] = value;
    configChangePubSub.publish("config-change");
  }, []);

  // effect to respond to changes in state that some other instance of this Provider
  // invoked
  useEffect(() => {
    const listener = () => {
      setState({ ...window.brazeDashboardConfig });
    };

    configChangePubSub.subscribe("config-change", listener);

    return () => {
      configChangePubSub.unsubscribe("config-change", listener);
    };
  }, []);

  return (
    <BrazeDashboardConfigContext.Provider value={[state, handleSetState]}>
      {children}
    </BrazeDashboardConfigContext.Provider>
  );
};
